// #4981 phase 3 (#5269) — every JUDGEMENT the Android SQLite migration makes.
//
// It imports only `local-database-engine-decisions.ts` and `config-record-resolve.ts`,
// neither of which reaches anything beyond the other, so this module loads under plain
// node and its whole decision surface is testable with no browser, no Capacitor and no
// PouchDB. The migration that DOES the work — opening databases, replicating,
// verifying — is `app/sqlite-migration.ts`. This is the same split phase 2 used, for
// the same reason.
//
// The requirement numbers below (R1, R2, R7, R8) come from the phase 3 PRD, which has
// been deleted now the code has landed. What those requirements settled is written into
// `design-docs/android-local-database-engine.md` — "Moving the data across (phase 3)",
// and in particular "Bounds, budgets, and the loops they close" for R8. Read that
// alongside this file; the numbers are kept only so the comments here stay readable
// against git history.

import { readUsableConfigNumber } from './config-record-resolve';
import { LocalDatabaseEngine } from './local-database-engine-decisions';

/**
 * How many boots may START a copy before the device gives up.
 *
 * Small, like `MAX_CLEANUP_ATTEMPTS` next door. Giving up costs the device nothing
 * it had — it stays on IndexedDB with all of its data, which is where it started.
 */
export const MAX_MIGRATION_COPY_ATTEMPTS = 3;

/**
 * How many consecutive aborts BEFORE a copy may happen before the device gives up.
 *
 * Larger than the copy budget, deliberately: these aborts are cheap (nothing was
 * copied) and a couple of them are plausible on a healthy device that happened to
 * boot while the store was busy. What this bounds is the device where they never
 * stop.
 */
export const MAX_MIGRATION_ABORTS = 5;

/**
 * The FLOOR the re-arm comparison falls back to, and the generation that holds every
 * rolled-back device.
 *
 * A device is held back while the generation it recorded is at or above the generation
 * currently in force, so a value of 0 holds everybody — every recorded generation is at
 * or above it.
 *
 * **Phase 4 (#5270) made the generation in force a CONFIG value, not this constant.**
 * `AndroidSqliteMigrationRearmGeneration` starts at 1, and raising it re-arms every
 * affected device at once, from the same place every other control lives. This constant
 * is what {@link resolveReArmGeneration} answers when that config value is missing or
 * cannot be read — the direction that keeps devices held. A per-device `localStorage`
 * marker is not a lever an operator can reach, which is why the hold is lifted by
 * comparison and never by deleting the key.
 */
export const MIGRATION_REARM_GENERATION = 0;

/**
 * The generation a rolled-back marker answers when it is present but cannot be read.
 *
 * **It is the highest, not the lowest, and the direction is the whole point.** The hold is
 * `the generation the device recorded >= the generation in force`, so the two sides of that
 * comparison want OPPOSITE extremes when they cannot be read. On the IN-FORCE side the
 * floor holds everybody, which is why {@link resolveReArmGeneration} answers
 * {@link MIGRATION_REARM_GENERATION}. On the RECORDED side the floor is the value LEAST
 * likely to hold — a damaged marker answering 0 against a configured 1 reads as re-armed,
 * which is exactly the migrate-roll-back-migrate lap the marker exists to stop.
 *
 * So an unreadable marker answers a generation no operator will ever configure past. A
 * device holding one is held until somebody looks at it, which is the right cost: a marker
 * that cannot be read is a device that was rolled back for a reason nobody can now see.
 */
export const UNKNOWN_ROLLBACK_GENERATION = Number.MAX_SAFE_INTEGER;

/**
 * Which half of the migration is running. (#5449)
 *
 * The copy used to be one thing that happened on one boot screen. It is now two, and
 * almost every rule below differs between them, so the phase is passed around rather
 * than inferred:
 *
 * - **{@link Background}** runs while the app is up and running on IndexedDB. It does
 *   the slow part — the bulk copy — in paced slices, and then follows the source so
 *   later writes keep reaching SQLite. It must NEVER write the engine marker, and it
 *   deliberately skips the "has anything asked the engine question?" guard, which is
 *   always tripped once the app is running.
 * - **{@link Switch}** runs at the top of a later page load, before anything has
 *   opened a database. It finishes what is outstanding, verifies it, and is the only
 *   half whose success lets the caller write the engine marker.
 */
export enum MigrationPhase {
  /** the bulk copy and the follow, while the app runs normally on IndexedDB */
  Background = 'Background',
  /** the top of a later page load: finish, verify, and hand back a marker-worthy answer */
  Switch = 'Switch',
}

/**
 * Why one migration attempt ended.
 *
 * An enum rather than strings, per CLAUDE.md: adding an outcome must be a compile
 * error at {@link abortSpendsAbortBudget}, not a silently-false answer that leaves a
 * device retrying for ever. Every value here is also a distinct telemetry name (R10),
 * because each one is a different repair.
 */
export enum MigrationOutcome {
  /** every database copied and verified, marker written */
  Completed = 'Completed',
  /** something already asked the engine question this page load (R3) */
  LatchAlreadyRead = 'LatchAlreadyRead',
  /** nobody is signed in, so there is no per-user config and no user database (R1) */
  NoSignedInUser = 'NoSignedInUser',
  /** `indexedDB.databases()` failed outright (R4) */
  EnumerationFailed = 'EnumerationFailed',
  /** the enumeration answered, but without this device's own user database (R4) */
  OwnDatabaseMissing = 'OwnDatabaseMissing',
  /** the enumeration answered, but without the common database the config lives in (R2) */
  CommonDatabaseMissing = 'CommonDatabaseMissing',
  /** the bounded config re-read failed or timed out (R2) */
  ConfigReadFailed = 'ConfigReadFailed',
  /** the config was read and now says do not migrate (R2) — the kill switch working */
  ConfigSaysNo = 'ConfigSaysNo',
  /** a document carrying attachments was found, which this adapter loses silently (R5) */
  AttachmentsFound = 'AttachmentsFound',
  /** a read or replication batch exceeded its bound (R5) */
  BoundHit = 'BoundHit',
  /** the copy did not match the source document for document (R6) */
  VerificationMismatch = 'VerificationMismatch',
  /** the copy failed for a reason that was NOT a bound being hit — a rejected write,
   *  a refused handle. Kept apart from {@link BoundHit} because phase 4's ramp counts
   *  them separately and "timed out" is a different repair from "the write failed". */
  CopyFailed = 'CopyFailed',
  /**
   * The copy finished, but something asked the engine question while it was running,
   * so this page load is committed to IndexedDB and the copy must not be believed (#5448).
   *
   * Writing the engine marker here would be silent data loss: the session keeps
   * reading and writing IndexedDB for the rest of its life, while the next boot comes
   * up on SQLite without any of it. The copy is thrown away instead, and a later boot
   * — one where nothing has asked yet — tries again.
   */
  LatchTakenDuringCopy = 'LatchTakenDuringCopy',
  /**
   * The switch half ran before the background half had finished and verified a bulk
   * copy of every database. (#5449)
   *
   * Not a failure and not a device problem — it is the ordinary state of a device
   * part-way through a copy that legitimately takes several sessions. The switch does
   * nothing, spends nothing, reports nothing, and the background half carries on.
   */
  BulkCopyNotReady = 'BulkCopyNotReady',
  /**
   * A kept SQLite target could no longer vouch for itself, so it was deleted and the
   * copy starts again on fresh handles. (#5449)
   *
   * Internal to one copy attempt — the caller re-enters immediately and never reports
   * or returns this. It exists as an outcome rather than a boolean so the re-entry is
   * visible at the call site instead of hidden inside the copy.
   */
  TargetRestarted = 'TargetRestarted',
  /**
   * The note saying "this device has a SQLite file for this database" could not be
   * written, so no copy was started. (#5449)
   *
   * Failing CLOSED is the point. Sign-out deletes these files by name, from that note —
   * the native plugin has no way to list them and the database provider never held
   * them. A target nobody recorded therefore survives sign-out with the previous user's
   * workouts and queued photos in it, waiting for whoever signs in next. Not copying
   * costs the device one background pass; copying without the note costs somebody
   * else's privacy.
   */
  TargetRecordFailed = 'TargetRecordFailed',
  /**
   * The app asked this copy to stop, because it is signing out or reloading. (#5449)
   *
   * Not a failure and not a device problem. The target keeps whatever it has and the
   * copy resumes from its checkpoint on a later page load, so nothing is spent and
   * nothing is reported. It exists so the copy can give up from wherever it had got
   * to, rather than carrying on writing into files the teardown is deleting.
   */
  Cancelled = 'Cancelled',
}

/**
 * Does this outcome spend the CONSECUTIVE-ABORT budget? (R8, as amended)
 *
 * **The rule is about WHERE the abort happened, not which one it is.** Every abort
 * from the start of the enumeration up to and including the bounded config read
 * spends it. That phrasing replaced a list which had to grow three times, each time
 * leaving another door open, and it is what makes a future abort added between those
 * two points bounded by default rather than by remembering to amend a list.
 *
 * The aborts that spend NOTHING all happen before the enumeration:
 *
 * - {@link MigrationOutcome.LatchAlreadyRead} — a wiring bug, not a device problem.
 *   Spending a budget on it would permanently disable migration for the device and
 *   report it identically to a real failure. It costs no boot time and shows up on
 *   the first device test, so it is deliberately left loud.
 * - {@link MigrationOutcome.NoSignedInUser} — not applicable, nothing was attempted.
 */
export function abortSpendsAbortBudget(outcome: MigrationOutcome): boolean {
  switch (outcome) {
    case MigrationOutcome.EnumerationFailed:
    case MigrationOutcome.OwnDatabaseMissing:
    case MigrationOutcome.CommonDatabaseMissing:
    case MigrationOutcome.ConfigReadFailed:
      return true;

    // The read ANSWERED, so the store is healthy and the abort is a deliberate
    // off-switch. It resets the counter rather than spending it — see
    // `abortResetsAbortBudget`.
    //
    // #5449 added the last three, and none of them is about the store at all: the
    // background half simply has not finished yet, the copy re-entered on fresh
    // handles, or a `localStorage` write failed. This budget exists to stop a device
    // whose store will not answer, so none of them may spend it.
    case MigrationOutcome.ConfigSaysNo:
    case MigrationOutcome.LatchAlreadyRead:
    case MigrationOutcome.NoSignedInUser:
    case MigrationOutcome.BulkCopyNotReady:
    case MigrationOutcome.TargetRestarted:
    case MigrationOutcome.TargetRecordFailed:
    case MigrationOutcome.Cancelled:
      return false;

    // everything past the config read is the copy budget's business, and that
    // budget is spent when the copy BEGINS rather than when it fails
    case MigrationOutcome.Completed:
    case MigrationOutcome.AttachmentsFound:
    case MigrationOutcome.BoundHit:
    case MigrationOutcome.VerificationMismatch:
    case MigrationOutcome.CopyFailed:
    case MigrationOutcome.LatchTakenDuringCopy:
      return false;
  }
}

/**
 * Does this outcome RESET the consecutive-abort budget? (R8, as amended)
 *
 * Exactly one thing resets it: the bounded config read returned an answer, yes or
 * no. A successful enumeration is NOT enough, and that is the whole point — a device
 * that enumerates fine every boot and then fails the config read every boot would
 * otherwise reset the counter to zero each time and loop for ever.
 *
 * Every outcome reachable only after the config read has answered therefore resets
 * it, including the copy failures: they prove the store answered.
 */
export function abortResetsAbortBudget(outcome: MigrationOutcome): boolean {
  switch (outcome) {
    case MigrationOutcome.ConfigSaysNo:
    case MigrationOutcome.Completed:
    case MigrationOutcome.AttachmentsFound:
    case MigrationOutcome.BoundHit:
    case MigrationOutcome.VerificationMismatch:
    case MigrationOutcome.CopyFailed:
    case MigrationOutcome.LatchTakenDuringCopy:
      return true;

    // #5449's three additions all sit here for one reason: none of them reached the
    // bounded config read, so none of them proves the store answered — which is the
    // only thing that resets this counter.
    case MigrationOutcome.EnumerationFailed:
    case MigrationOutcome.OwnDatabaseMissing:
    case MigrationOutcome.CommonDatabaseMissing:
    case MigrationOutcome.ConfigReadFailed:
    case MigrationOutcome.LatchAlreadyRead:
    case MigrationOutcome.NoSignedInUser:
    case MigrationOutcome.BulkCopyNotReady:
    case MigrationOutcome.TargetRestarted:
    case MigrationOutcome.TargetRecordFailed:
    case MigrationOutcome.Cancelled:
      return false;
  }
}

/**
 * Does this outcome SPEND the copy budget? (R8)
 *
 * The budget is incremented when the copy begins, before the first source read, so a
 * boot killed mid-copy still spends an attempt. That means every outcome which ended
 * BEFORE any copying has to give the attempt back — otherwise a device that never gets
 * as far as copying would exhaust the copy budget on aborts alone, which is what the
 * separate abort counter exists to prevent.
 *
 * **This is deliberately NOT `!abortResetsAbortBudget(outcome)`.** That function
 * answers a different question — whether the bounded config read ANSWERED — and it is
 * true for the three genuine copy failures below, because reaching them proves the
 * store answered. Deciding the refund from it therefore hands the attempt back on
 * exactly the failures that must consume it, and the copy budget can never run out:
 * a device that hits a bound, a mismatch or an attachment on every launch re-requests
 * and re-copies for ever. The two questions are asked separately because they have
 * different answers, and this comment exists because collapsing them looks like a
 * simplification.
 */
export function outcomeSpendsCopyBudget(outcome: MigrationOutcome): boolean {
  switch (outcome) {
    // the three genuine copy failures R8 names. The attachment abort counts even
    // though it runs just BEFORE the replication: it is a per-database scan of the
    // real data, it costs what a copy costs, and a device holding an attachment will
    // abort identically on every attempt.
    case MigrationOutcome.AttachmentsFound:
    case MigrationOutcome.BoundHit:
    case MigrationOutcome.VerificationMismatch:
    case MigrationOutcome.CopyFailed:
      return true;

    // the copy itself worked; what went wrong was outside it, and a later boot where
    // nothing has asked the engine question yet has every chance of succeeding. So the
    // attempt is handed back rather than spent (#5448)
    case MigrationOutcome.LatchTakenDuringCopy:
      return false;

    // Ended before any copying began — the attempt is refunded and, where it applies,
    // the consecutive-abort counter is what bounds the device instead.
    //
    // #5449 added the last three, and no copy was started in any of them either: the
    // background half had not finished, the copy re-entered on fresh handles, or the
    // target was never created because its name could not be recorded.
    case MigrationOutcome.EnumerationFailed:
    case MigrationOutcome.OwnDatabaseMissing:
    case MigrationOutcome.CommonDatabaseMissing:
    case MigrationOutcome.ConfigReadFailed:
    case MigrationOutcome.ConfigSaysNo:
    case MigrationOutcome.LatchAlreadyRead:
    case MigrationOutcome.NoSignedInUser:
    case MigrationOutcome.BulkCopyNotReady:
    case MigrationOutcome.TargetRestarted:
    case MigrationOutcome.TargetRecordFailed:
    case MigrationOutcome.Cancelled:
      return false;

    // a completed migration clears the counter outright rather than refunding one
    case MigrationOutcome.Completed:
      return false;
  }
}

/** Has the copy budget run out? */
export function isCopyBudgetExhausted(attempts: number): boolean {
  return attempts >= MAX_MIGRATION_COPY_ATTEMPTS;
}

/** Has the consecutive-abort budget run out? */
export function isAbortBudgetExhausted(aborts: number): boolean {
  return aborts >= MAX_MIGRATION_ABORTS;
}

/**
 * Read a counter out of `localStorage`, treating anything unreadable as zero.
 *
 * Copied in spirit from `readAttemptCount` in `legacy-idb-cleanup.ts`. A damaged or
 * older-shaped value must not throw on the boot path, and must not read as a huge
 * number that silently disables the migration for ever.
 */
export function readMigrationCounter(storedValue: string | null | undefined): number {
  const parsed = Number.parseInt(storedValue ?? '', 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

/**
 * Is this device held back by phase 4's rolled-back marker? (R1)
 *
 * Phase 3 never writes the marker; it only refuses to migrate while one is held.
 * Without this, a device phase 4 rolled back sits in exactly the state the deciding
 * check fires on and copies its whole database again on the very next page load.
 *
 * **An unparseable marker counts as HELD.** That is the cautious direction here: the
 * cost of wrongly holding is that a device does not migrate, which leaves it exactly
 * where it already is with all of its data; the cost of wrongly releasing is copying
 * a whole database on every page load. Compare `.claude-local` marker handling in
 * CLAUDE.md, which resolves an unreadable marker to whichever answer asks a person
 * more often — same principle, applied to whichever answer does less.
 *
 * The marker's own shape is phase 4's business. This reads a `generation` out of
 * either a JSON object (phase 4 also keeps superseded database names in it) or a
 * bare number, and holds while that generation is at or above this build's re-arm
 * generation.
 */
export function isMigrationHeldByRollback(
  markerValue: string | null | undefined,
  reArmGeneration: number = MIGRATION_REARM_GENERATION,
): boolean {
  // no marker at all is the normal case — nothing has ever rolled this device back
  if (markerValue === null || markerValue === undefined || markerValue === '') return false;

  const generation = readRollbackGeneration(markerValue);
  // unparseable: a marker IS present, so this device was rolled back; only its
  // generation is unreadable. Hold.
  if (generation === undefined) return true;

  return generation >= reArmGeneration;
}

/** Pull the re-arm generation out of a rolled-back marker, or undefined if it cannot be read. */
function readRollbackGeneration(markerValue: string): number | undefined {
  const asBareNumber = Number.parseInt(markerValue, 10);
  if (Number.isFinite(asBareNumber) && String(asBareNumber) === markerValue.trim()) {
    return asBareNumber;
  }

  try {
    const parsed = JSON.parse(markerValue);
    if (parsed && typeof parsed === 'object' && typeof parsed.generation === 'number') {
      return parsed.generation;
    }
  } catch {
    // fall through to "cannot be read", which holds. Deliberately not reported from
    // here: this module is pure, and the caller reports what it decides.
  }
  return undefined;
}

/** What the deciding boot knows when it asks whether to request a migration. */
export interface MigrationRequestInput {
  /** Capacitor native AND not iOS — the only devices this migration is for */
  isAndroidNative: boolean;
  /** where this device's data is RIGHT NOW, from the phase 2 marker */
  currentEngine: LocalDatabaseEngine;
  /** the signed-in user, or undefined when nobody is */
  uid: string | undefined;
  /** raw value of the do-not-request-again marker */
  blockedMarker: string | null | undefined;
  /** raw value of phase 4's rolled-back marker */
  rolledBackMarker: string | null | undefined;
  /**
   * The re-arm generation currently in force, from
   * `ConfigKey.AndroidSqliteMigrationRearmGeneration` through
   * {@link resolveReArmGeneration}. (#5270)
   *
   * Omitted means the floor, which holds every rolled-back device — the direction a
   * missing or unreadable value must take.
   */
  reArmGeneration?: number;
  /** what `shouldMigrateThisDeviceToSqlite` answered for the two rollout controls */
  controlsSayMigrate: boolean;
}

/**
 * Should the DECIDING boot write the migration-requested marker? (R1)
 *
 * The order of these checks is not arbitrary — the two markers are asked FIRST,
 * before the rollout controls, because both mean "this device has already been
 * through something and must not be put through it again", whatever the controls
 * currently say.
 *
 * Deliberately cheap: every check here is a synchronous `localStorage` read or a
 * value already in hand, so an Android device that is not migrating pays nothing on
 * its boot path. Reading the config on every Android boot instead would put a new
 * blocking database read in front of exactly the devices whose local store sometimes
 * stops answering.
 */
export function shouldRequestMigration(input: MigrationRequestInput): boolean {
  // phase 4 rolled this device back. Only an explicit re-arm lifts that, and the
  // generation in force comes from config (#5270) — an absent value here is the floor,
  // which holds.
  if (isMigrationHeldByRollback(input.rolledBackMarker, input.reArmGeneration)) return false;

  // a budget ran out on this device. Same reasoning: do not start again.
  if (input.blockedMarker) return false;

  // this migration is Android-only. iOS has been on SQLite since before any of this
  // existed, and a plain browser has no adapter to move to.
  if (!input.isAndroidNative) return false;

  // already on SQLite: there is nothing to copy, and copying would overwrite the
  // real data with a stale IndexedDB copy
  if (input.currentEngine !== LocalDatabaseEngine.IndexedDb) return false;

  // no signed-in user means no per-user config answer and no user database. Not a
  // failure — "not applicable".
  if (!input.uid) return false;

  return input.controlsSayMigrate;
}

/** What the migrating boot knows before it starts. */
export interface MigrationRunInput {
  /** raw value of the migration-requested marker */
  requestedMarker: string | null | undefined;
  /** raw value of the do-not-request-again marker */
  blockedMarker: string | null | undefined;
  isAndroidNative: boolean;
  currentEngine: LocalDatabaseEngine;
  uid: string | undefined;
  /** has this page load already run the migration? */
  alreadyRanThisPageLoad: boolean;
}

/**
 * Should THIS boot perform the copy? (R1)
 *
 * The engine check is the load-bearing one and it is not redundant with the request
 * marker: a device that migrated successfully has its request marker cleared, but if
 * anything ever left both set, running again would copy a stale IndexedDB database
 * over the live SQLite one.
 *
 * The blocked marker is re-checked here as well as in {@link shouldRequestMigration}
 * so that neither `localStorage` survivor path has to restore the two markers
 * consistently with each other for the hold to work.
 */
export function shouldRunMigrationThisBoot(input: MigrationRunInput): boolean {
  // once per page load. The single call site sits above a branch both paths take,
  // and the warm-resume failure fallback can re-enter the surrounding method.
  if (input.alreadyRanThisPageLoad) return false;

  if (!input.requestedMarker) return false;
  if (input.blockedMarker) return false;
  if (!input.isAndroidNative) return false;
  if (input.currentEngine !== LocalDatabaseEngine.IndexedDb) return false;
  if (!input.uid) return false;

  return true;
}

// ---------------------------------------------------------------------------
// R6 — the verification comparison.
// ---------------------------------------------------------------------------

/** One document as the changes feed describes it. */
export interface DocumentLeafSummary {
  /** every leaf revision, SORTED — a set, not "the winning revision" */
  leafRevs: string[];
  /** whether the winning revision is a deletion */
  deleted: boolean;
}

/** What a verification found. Empty `differences` means the two sides matched. */
export interface VerificationResult {
  matched: boolean;
  sourceCount: number;
  targetCount: number;
  /** how many document ids differed in any way */
  differenceCount: number;
  /** a BOUNDED sample of the ids that differed — never document contents */
  sampleIds: string[];
  /** which way each sampled id differed, for the report */
  sampleReasons: string[];
}

/** How many differing ids a report may name. Bounded so a wholly-failed copy cannot
 *  put a whole database's worth of ids into a telemetry payload. */
export const VERIFICATION_SAMPLE_LIMIT = 10;

/**
 * Do the two sides hold the same documents? (R6)
 *
 * **Compares the SET of leaf revisions per document id, not "the revision".** A
 * document with a conflict has more than one leaf, and comparing only the winner
 * would pass a copy that had silently dropped the losing branch — which is the main
 * reason this phase copies by replication in the first place, so a verification
 * blind to it would check the one thing it exists to check.
 *
 * Never puts document contents in the result: only ids, counts, and which way each
 * sampled id differed.
 */
export function verifyDatabaseContents(
  source: Map<string, DocumentLeafSummary>,
  target: Map<string, DocumentLeafSummary>,
): VerificationResult {
  const sampleIds: string[] = [];
  const sampleReasons: string[] = [];
  let differenceCount = 0;

  function noteDifference(docId: string, reason: string): void {
    differenceCount += 1;
    if (sampleIds.length < VERIFICATION_SAMPLE_LIMIT) {
      sampleIds.push(docId);
      sampleReasons.push(`${docId}=${reason}`);
    }
  }

  for (const [docId, sourceSummary] of source) {
    const targetSummary = target.get(docId);
    if (!targetSummary) {
      noteDifference(docId, 'missing');
      continue;
    }
    if (sourceSummary.deleted !== targetSummary.deleted) {
      noteDifference(docId, 'deleted-flag');
      continue;
    }
    if (!sameStringSet(sourceSummary.leafRevs, targetSummary.leafRevs)) {
      noteDifference(docId, 'leaf-revs');
    }
  }

  // an EXTRA document in the target is a difference too. It should be impossible,
  // and if it happens the target is not a copy of the source and nothing about it
  // should be believed.
  for (const docId of target.keys()) {
    if (!source.has(docId)) noteDifference(docId, 'extra');
  }

  return {
    matched: differenceCount === 0,
    sourceCount: source.size,
    targetCount: target.size,
    differenceCount,
    sampleIds,
    sampleReasons,
  };
}

/** Are these two already-sorted revision lists the same set? */
function sameStringSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let index = 0; index < a.length; index++) {
    if (a[index] !== b[index]) return false;
  }
  return true;
}

/**
 * Do the two sides hold the same `_local/` documents? (R5b, R6)
 *
 * **Compared by BODY, never by revision.** A `_local/` document's revision is a
 * locally generated counter — `planLocalWrite` computes `0-<n+1>` — so a document
 * sitting at `0-42` in the source becomes `0-1` in the target on a correct copy. A
 * revision comparison would therefore fail on every device. It fails closed, which is
 * the safe direction, but it would leave the phase non-functional and the tempting
 * "fix" under pressure is to weaken the verification instead.
 *
 * `_rev` is stripped from both sides before comparing, because `get()` returns it on
 * the object and it is exactly the field that can never match.
 */
export function verifyLocalDocuments(
  source: Map<string, any>,
  target: Map<string, any>,
): VerificationResult {
  const sampleIds: string[] = [];
  const sampleReasons: string[] = [];
  let differenceCount = 0;

  function noteDifference(docId: string, reason: string): void {
    differenceCount += 1;
    if (sampleIds.length < VERIFICATION_SAMPLE_LIMIT) {
      sampleIds.push(docId);
      sampleReasons.push(`${docId}=${reason}`);
    }
  }

  for (const [docId, sourceDoc] of source) {
    if (!target.has(docId)) {
      noteDifference(docId, 'missing');
      continue;
    }
    if (!sameLocalDocumentBody(sourceDoc, target.get(docId))) {
      noteDifference(docId, 'body');
    }
  }
  for (const docId of target.keys()) {
    if (!source.has(docId)) noteDifference(docId, 'extra');
  }

  return {
    matched: differenceCount === 0,
    sourceCount: source.size,
    targetCount: target.size,
    differenceCount,
    sampleIds,
    sampleReasons,
  };
}

/**
 * Are these two `_local/` documents the same, ignoring `_rev`?
 *
 * A stable-key JSON comparison rather than a deep walk: these documents are small
 * and plain (the conflict archive index, a scanner preference), and a hand-written
 * deep-equality routine here would be more code to get subtly wrong than the thing it
 * is checking.
 */
export function sameLocalDocumentBody(a: any, b: any): boolean {
  return stableStringifyWithoutRev(a) === stableStringifyWithoutRev(b);
}

/** JSON with object keys in a fixed order and `_rev` removed at every level. */
function stableStringifyWithoutRev(value: any): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'undefined';
  if (Array.isArray(value)) {
    return `[${value.map(stableStringifyWithoutRev).join(',')}]`;
  }
  const keys = Object.keys(value).filter(key => key !== '_rev').sort();
  return `{${keys.map(key => `${JSON.stringify(key)}:${stableStringifyWithoutRev(value[key])}`).join(',')}}`;
}

/**
 * The `_local/` documents that are deliberately NOT copied, and why. (R5b)
 *
 * Stated as data rather than prose so the migration and its tests agree about it,
 * and so a reader adding a new `_local/` document can see whether it belongs here.
 *
 * - **replication checkpoints** (`_local/<hash>` written by PouchDB) — they record
 *   how far a replication with the SERVER got, and the new database has its own
 *   replication identity. Carrying one would make the first sync believe it had
 *   already fetched things it has not.
 * - **`_local/view-heal`** — the view-index heal salt. The views themselves are not
 *   copied and rebuild from the documents, so the salt is regenerable. R12b writes a
 *   fresh one deliberately, with the current global rebuild version stamped in.
 * - **`_local/mrviews`** — PouchDB's own view-index bookkeeping, same reasoning.
 */
export const LOCAL_DOCS_DELIBERATELY_NOT_COPIED: readonly string[] = [
  '_local/view-heal',
  '_local/mrviews',
];

/**
 * Is this `_local/` document one the copy should skip? (R5b)
 *
 * Replication checkpoints cannot be listed by name — PouchDB derives the id from a
 * hash of the replication's identity — so they are recognised by shape instead: a
 * checkpoint carries a `last_seq`, which none of this app's own `_local/` documents
 * does.
 */
export function isLocalDocDeliberatelyNotCopied(docId: string, doc: any): boolean {
  if (LOCAL_DOCS_DELIBERATELY_NOT_COPIED.includes(docId)) return true;
  // a replication checkpoint, whatever its hashed id
  if (doc && typeof doc === 'object' && doc.last_seq !== undefined) return true;
  return false;
}

// ─── #5449 — the SQLite files a BACKGROUND copy leaves on the device ──────

/**
 * Read the list of SQLite targets a background copy has written into. (#5449)
 *
 * Pure, so the sign-out path can be tested with no browser. The caller reads
 * `SQLITE_MIGRATION_TARGETS_KEY` from `localStorage` and passes the raw string in; a
 * read that threw arrives here as `null`.
 *
 * **Every damaged shape answers "nothing recorded" rather than throwing.** This runs
 * on the sign-out wipe, where a throw would abandon the rest of the teardown and leave
 * far more of the previous user's data on the device than the one unreadable value
 * cost. The value may have been written by an older version of the app, or half-written
 * by a boot that was killed, so it is normalised on the way in rather than trusted:
 * anything that is not an array of usable names is dropped, one bad entry at a time,
 * so a single unusable entry does not cost the names beside it.
 */
export function readMigrationTargetNames(storedValue: string | null | undefined): string[] {
  if (!storedValue) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(storedValue);
  } catch {
    // not JSON at all. There is nothing to recover, and the names are already lost;
    // saying so quietly is the whole point of this guard.
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  return parsed.filter((name): name is string => typeof name === 'string' && name.length > 0);
}

/**
 * Add one target name to the record, or say there is nothing to write. (#5449)
 *
 * Answers the new value to store, or `undefined` when the name is already recorded —
 * every boot re-opens the same targets, so without that the value would be rewritten on
 * every pass for no change.
 *
 * A damaged existing value is REBUILT around the new name rather than preserved. The
 * names it held are unreadable and therefore already lost; what matters is that the
 * target being opened right now is recorded, because that is the one sign-out has to be
 * able to delete.
 */
export function recordMigrationTargetName(
  storedValue: string | null | undefined,
  logicalName: string,
): string | undefined {
  const existing = readMigrationTargetNames(storedValue);
  if (existing.includes(logicalName)) return undefined;
  return JSON.stringify([...existing, logicalName]);
}

// ─── df-a00755b05133 — the coach copies manage mode leaves on the device ──

/**
 * Read the list of coach copies recorded in `SQLITE_COACH_COPY_NAMES_KEY`.
 * (df-a00755b05133)
 *
 * The note has the same shape as the background-copy targets, and it is read on the same
 * sign-out wipe, so it gets the same treatment: every damaged shape answers "nothing
 * recorded" rather than throwing.
 */
export function readCoachCopyNames(storedValue: string | null | undefined): string[] {
  return readMigrationTargetNames(storedValue);
}

/**
 * Add one coach copy to the record, or answer `undefined` when it is already there.
 * (df-a00755b05133)
 *
 * Same rules as `recordMigrationTargetName`: every entry into manage mode re-opens the
 * same copy, so an unchanged list is not rewritten, and a damaged value is rebuilt
 * around the name being opened now.
 */
export function recordCoachCopyName(
  storedValue: string | null | undefined,
  logicalName: string,
): string | undefined {
  return recordMigrationTargetName(storedValue, logicalName);
}

/**
 * Must sign-out delete this device's SQLite files? (#5449)
 *
 * Three facts, any one of which is enough. The second is the one #5449 adds:
 *
 * - **The device is already on SQLite.** Its live data is there, so of course.
 * - **A background copy has a target on disk**, even though the device is still
 *   reading and writing IndexedDB. Before #5449 this could not happen: the copy ran on
 *   the splash and a failed attempt deleted its target before the next try, so a target
 *   never outlived the boot that made it. Now it does, on purpose, so the copy can
 *   resume rather than restart. The engine alone is therefore no longer the right
 *   question — asking it would load no deleter at all and leave a half-finished copy of
 *   the signed-in user's workouts and queued photos on the device for the next person.
 * - **Phase 4 rolled the device back AND a coach copy is recorded.** (df-4576933c7cf2)
 *   The rollback copies back and deletes only the background-copy targets. A coach's
 *   copy of a client database, opened while the device was on SQLite and closed by
 *   "Back to my account", is not one of them, so it stays on disk as a SQLite file.
 *   After the rollback the engine says IndexedDB and the target list is gone, so
 *   without this fact sign-out would leave the client's whole history on the device.
 *
 * Answering `false` when none holds is not a micro-optimisation: it keeps every
 * browser, and every Android device the rollout has not reached, from loading the
 * native SQLite plugin during sign-out for files that do not exist. That is why the
 * third fact needs BOTH halves: only Android writes the rolled-back marker, and a
 * rolled-back device with no coach copies has nothing left to delete.
 */
export function signOutMustDeleteSqliteFiles(
  engine: LocalDatabaseEngine,
  recordedTargetNames: readonly string[],
  wasRolledBack: boolean,
  coachCopyNames: readonly string[],
): boolean {
  return engine === LocalDatabaseEngine.Sqlite
    || recordedTargetNames.length > 0
    || (wasRolledBack && coachCopyNames.length > 0);
}

// ─── #5449 — what the background half records about each target ───────────

/**
 * What the background copy knows about one SQLite target it has been filling.
 *
 * Kept as a `_local/` document INSIDE that target rather than in `localStorage`, for
 * one reason: it must not be able to outlive the thing it describes. A target deleted
 * on a device whose `localStorage` survived would otherwise leave a record claiming a
 * verified copy that is no longer there, and the switch would believe it.
 */
export interface MigrationTargetState {
  /** has the bulk copy finished AND passed a full document-for-document verification? */
  bulkCopyVerified: boolean;
  /**
   * The source sequence the full verification covered — `S`.
   *
   * The switch half re-checks only the documents that changed after this point, which
   * is what keeps a full walk of both databases off the boot screen. It means the
   * full comparison must genuinely have happened at `S`; see the design doc.
   */
  verifiedThroughSeq: unknown;
  /** the source database's own id, to notice a source that has been rebuilt */
  sourceDbId: string;
  /** the target database's own id, to notice a target that has been replaced */
  targetDbId: string;
  /** the app version that wrote this, to notice a storage-format change */
  appVersion: string;
}

/**
 * Read a target's state document for what it actually holds. (#5449)
 *
 * Defensive on purpose, and not as a formality: this document was written by whichever
 * version of the app the user last ran, and it may have been half-written by a boot
 * that was killed mid-copy. **Every unreadable shape answers `undefined`**, which the
 * caller treats as "this target cannot be trusted" — the safe direction, because it
 * leads to deleting the target and copying again rather than to believing a copy
 * nobody verified.
 *
 * `bulkCopyVerified` must be exactly `true`; anything else, including a missing field,
 * reads as not verified.
 */
export function readMigrationTargetState(doc: any): MigrationTargetState | undefined {
  if (!doc || typeof doc !== 'object') return undefined;
  if (typeof doc.sourceDbId !== 'string' || doc.sourceDbId.length === 0) return undefined;
  if (typeof doc.targetDbId !== 'string' || doc.targetDbId.length === 0) return undefined;
  if (typeof doc.appVersion !== 'string') return undefined;
  return {
    bulkCopyVerified: doc.bulkCopyVerified === true,
    verifiedThroughSeq: doc.verifiedThroughSeq,
    sourceDbId: doc.sourceDbId,
    targetDbId: doc.targetDbId,
    appVersion: doc.appVersion,
  };
}

/** Everything {@link shouldDeleteExistingTarget} weighs. */
export interface ExistingTargetInput {
  /** what the target says about itself, or `undefined` if it said nothing readable */
  state: MigrationTargetState | undefined;
  /** does the target hold anything at all? */
  targetHasContent: boolean;
  /** the source database's id as it is right now */
  sourceDbId: string;
  /** the target database's id as it is right now */
  targetDbId: string;
  /** the app version running now */
  appVersion: string;
}

/**
 * Must this partially filled SQLite target be thrown away and started again? (#5449)
 *
 * Before #5449 the answer was always yes: the target was deleted before every attempt,
 * so a copy always wrote into an empty database. Keeping it is what lets a background
 * copy resume from its checkpoint instead of restarting from zero on a database that
 * takes minutes — which is the whole point of the ticket. So the question is now real,
 * and getting it wrong in the lenient direction means believing a copy that is not a
 * copy of this user's data.
 *
 * Each rule stands in front of a specific way a kept target goes bad:
 *
 * - **The target holds documents but says nothing about itself.** It predates this
 *   record, or its state document was lost. Nothing can vouch for what is in it.
 * - **The source has a different id than the one recorded.** The source database was
 *   destroyed and rebuilt under the same name — which is exactly what sign-out and a
 *   fresh sign-in do. Replication only ever ADDS, so branches the old source held and
 *   the new one does not would sit in the target for ever, failing verification as
 *   `extra` on every attempt.
 * - **The target has a different id than the one recorded.** Something replaced the
 *   file underneath us; the checkpoints in the source no longer describe it.
 * - **The app version has changed.** A storage-format change between versions is not
 *   something this code can detect from the outside, and re-copying costs one
 *   background pass. Cheap insurance against an expensive, silent kind of wrong.
 *
 * An EMPTY target with no state is not deleted — there is nothing to delete, and
 * asking the native plugin to remove a file that does not exist just invites a
 * spurious failure report.
 */
export function shouldDeleteExistingTarget(input: ExistingTargetInput): boolean {
  if (!input.state) return input.targetHasContent;
  if (input.state.sourceDbId !== input.sourceDbId) return true;
  if (input.state.targetDbId !== input.targetDbId) return true;
  if (input.state.appVersion !== input.appVersion) return true;
  return false;
}

// ---------------------------------------------------------------------------
// #4981 phase 4 (#5270) — the way back.
//
// Everything below belongs to the REVERSE copy: moving a device off SQLite and back
// onto the browser's IndexedDB. `app/sqlite-migration-reverse.ts` is what does that
// work; this is what it decides.
//
// R2 is the phase 4 PRD's requirement number; that PRD has been deleted now the code has
// landed. Read `design-docs/android-local-database-engine.md`, "The way back, the ramp,
// and the cleanup (phase 4, #5270)", alongside this section.
// ---------------------------------------------------------------------------

/**
 * Why one rollback attempt ended.
 *
 * **A separate enum from {@link MigrationOutcome}, on purpose.** The two directions
 * have different triggers, different risks and different budgets, and the plan's
 * "Refactor Before Implementing" section forbids merging them while the reverse one
 * is still unproven on a real device. Merging the enums would also drag the reverse
 * direction's outcomes into the forward direction's three budget switches, where they
 * can never occur and would have to be answered anyway.
 *
 * The values that read like {@link MigrationOutcome}'s do mean the same thing, and the
 * reverse copy maps the shared copy machinery's answers onto them in ONE place, so the
 * translation is visible instead of scattered. Every value here is a distinct telemetry
 * name, because each one is a different repair — which is what lets phase 4's ramp count
 * rollback failures apart from migration failures.
 */
export enum RollbackOutcome {
  /** every database copied back, verified, and the device is on IndexedDB again */
  Completed = 'Completed',
  /** nobody is signed in, so there is no per-user config and no user database */
  NoSignedInUser = 'NoSignedInUser',
  /** the config was read and now says do not roll back — the kill switch working */
  ConfigSaysNo = 'ConfigSaysNo',
  /** the bounded config re-read failed or timed out */
  ConfigReadFailed = 'ConfigReadFailed',
  /** nothing on the device names the SQLite databases to read from */
  NoSourcesRecorded = 'NoSourcesRecorded',
  /** something already asked the engine question this page load */
  LatchAlreadyRead = 'LatchAlreadyRead',
  /** something asked the engine question while the switch half was running */
  LatchTakenDuringCopy = 'LatchTakenDuringCopy',
  /** a document carrying attachments was found, which this adapter loses silently */
  AttachmentsFound = 'AttachmentsFound',
  /** a read or replication batch exceeded its bound */
  BoundHit = 'BoundHit',
  /** the copy back did not match the SQLite source document for document */
  VerificationMismatch = 'VerificationMismatch',
  /** the copy back failed for a reason that was NOT a bound being hit */
  CopyFailed = 'CopyFailed',
  /** the background half has not finished and verified every database yet */
  BulkCopyNotReady = 'BulkCopyNotReady',
  /** a kept IndexedDB target could no longer vouch for itself and was thrown away */
  TargetRestarted = 'TargetRestarted',
  /** the app asked this copy to stop, because it is signing out or reloading */
  Cancelled = 'Cancelled',
  /**
   * The rollback-completion record could not be written, so nothing was committed.
   *
   * Fails CLOSED, like the forward direction's target record. The record IS the commit
   * point, and without it a boot killed part-way through the tail has nothing to tell the
   * next boot what to finish. The copy that was made is simply left where it is — the
   * device still reads SQLite, nothing has changed underneath it, and the next page load
   * tries again.
   */
  RecordWriteFailed = 'RecordWriteFailed',
  /**
   * `bumpLocalStoreGeneration` did not leave the device on the name the record says.
   *
   * It is documented to leave the app on the OLD name and return it when the write is
   * refused. Carrying on would flip the engine marker and delete the SQLite files while
   * the device still reads the pre-rollback generation — the very databases it is about
   * to need. So the tail stops here, leaving the record in place, and the next boot
   * retries from step 3.
   */
  GenerationBumpMismatch = 'GenerationBumpMismatch',
  /**
   * The re-arm generation this rollback should record could not be read, so nothing was
   * committed.
   *
   * The generation is decided on the session that ASKS for the rollback, where the config
   * is loaded, and carried to the page load that ACTS in the request marker — the boot
   * path cannot read config, and a third database read at the top of a boot is exactly
   * what this project exists to remove.
   *
   * **It fails closed because the cautious direction is not available any other way.** The
   * hold works by comparison: a device is held while the generation it recorded is at or
   * above the one in force. Recording a guess that is too LOW re-arms the device the
   * moment the operator raises the dial by one, which is the migrate-roll-back-migrate lap
   * the marker exists to stop, and there is no "safely high" number to guess instead. So
   * the rollback stops, leaving the device on SQLite with everything it had, and the next
   * deciding session writes a readable request marker.
   */
  ReArmGenerationUnknown = 'ReArmGenerationUnknown',
  /**
   * The device's stored #4401 generation is neither the recorded target nor one below it,
   * so the completion record cannot describe this device and the tail stops without
   * touching anything.
   *
   * **Why this has to be its own answer, rather than being discovered after the bump.**
   * `bumpLocalStoreGeneration` increments UNCONDITIONALLY and moves the app onto the new
   * name, so checking afterwards is already too late: a device two or more generations
   * below the target would be pushed one step closer on every boot, and each of those
   * boots opens a database at a name nothing has ever written — an empty app that
   * re-syncs from the server and loses whatever had never left the phone.
   *
   * It is reachable rather than theoretical. The completion record is on both
   * `localStorage` survivor lists and the generation keys are on neither, so a sign-out
   * wipe leaves the record naming a target the device is several generations below.
   *
   * The record is KEPT, because it still holds the only names that can find the
   * superseded SQLite databases. What it needs is a person, which is what the report is
   * for — so this reports on every boot rather than escalating on every boot.
   */
  GenerationOutOfRange = 'GenerationOutOfRange',
  /**
   * A `localStorage` write in the tail's steps 3-4 threw, so the tail stopped part-way.
   *
   * A browser with site data blocked throws from the setter itself, and so does an
   * exhausted quota. Answering with an outcome rather than letting the throw travel is what
   * keeps `runSqliteRollbackAsync`'s "never throws" contract honest on the boot path — and
   * the state it stops in is survivable by construction: the completion record is still
   * there, so the next boot re-runs the whole tail, and every step of it is idempotent.
   */
  TailWriteFailed = 'TailWriteFailed',
}

/**
 * One problem name per cause (R2). Deliberately PARTIAL, like the forward direction's:
 * `Completed`, `ConfigSaysNo`, `NoSignedInUser`, `BulkCopyNotReady` and
 * `Cancelled` are ordinary states rather than problems and have no name here.
 *
 * Every name is prefixed `AndroidSqliteRollback`, never `AndroidSqliteMigration`, so the
 * ramp reads rollback failures apart from migration failures at a glance.
 */
export const ROLLBACK_PROBLEM_NAMES: Readonly<Partial<Record<RollbackOutcome, string>>> = {
  [RollbackOutcome.ConfigReadFailed]: 'AndroidSqliteRollbackConfigReadFailed',
  [RollbackOutcome.NoSourcesRecorded]: 'AndroidSqliteRollbackNoSourcesRecorded',
  [RollbackOutcome.LatchAlreadyRead]: 'AndroidSqliteRollbackLatchAlreadyRead',
  [RollbackOutcome.LatchTakenDuringCopy]: 'AndroidSqliteRollbackLatchTakenDuringCopy',
  [RollbackOutcome.AttachmentsFound]: 'AndroidSqliteRollbackAttachmentsFound',
  [RollbackOutcome.BoundHit]: 'AndroidSqliteRollbackBoundHit',
  [RollbackOutcome.VerificationMismatch]: 'AndroidSqliteRollbackVerificationMismatch',
  [RollbackOutcome.CopyFailed]: 'AndroidSqliteRollbackCopyFailed',
  [RollbackOutcome.TargetRestarted]: 'AndroidSqliteRollbackTargetRestarted',
  [RollbackOutcome.RecordWriteFailed]: 'AndroidSqliteRollbackRecordWriteFailed',
  [RollbackOutcome.GenerationBumpMismatch]: 'AndroidSqliteRollbackGenerationBumpMismatch',
  [RollbackOutcome.ReArmGenerationUnknown]: 'AndroidSqliteRollbackReArmGenerationUnknown',
  [RollbackOutcome.GenerationOutOfRange]: 'AndroidSqliteRollbackGenerationOutOfRange',
  [RollbackOutcome.TailWriteFailed]: 'AndroidSqliteRollbackTailWriteFailed',
};

/** Telemetry name for a completed rollback. */
export const ROLLBACK_COMPLETED_INFO = 'AndroidSqliteRollbackCompleted';

/**
 * Does this outcome SPEND the rollback's copy budget?
 *
 * **The mirror of {@link outcomeSpendsCopyBudget}, and it exists because leaving it out was
 * a real bug rather than an omission.** The budget is spent before the first read, so a page
 * load killed mid-copy still counts — which means every outcome that ended BEFORE any
 * copying has to hand the attempt back. Without that, the ordinary answer a device gives on
 * every page load while its background copy is still running ({@link
 * RollbackOutcome.BulkCopyNotReady}) would spend an attempt each time. The budget is 3 and
 * the copy legitimately takes several sessions, so most devices would arrive at their first
 * real attempt with the budget already gone and be blocked from ever rolling back — on the
 * one control an operator reaches for during an incident.
 *
 * Only the outcomes where the copy itself was tried and failed spend anything.
 */
export function rollbackOutcomeSpendsCopyBudget(outcome: RollbackOutcome): boolean {
  switch (outcome) {
    // the copy was really attempted and really failed. A device that hits one of these on
    // every launch is the device this budget exists to stop.
    case RollbackOutcome.AttachmentsFound:
    case RollbackOutcome.BoundHit:
    case RollbackOutcome.VerificationMismatch:
    case RollbackOutcome.CopyFailed:
      return true;

    // The copy worked; what went wrong was outside it, or nothing was attempted at all. A
    // later page load has every chance of succeeding, so the attempt is handed back.
    // `BulkCopyNotReady` is the important one — it is the ordinary state of a device
    // part-way through a copy that takes several sessions, not a failure.
    case RollbackOutcome.Completed:
    case RollbackOutcome.BulkCopyNotReady:
    case RollbackOutcome.Cancelled:
    case RollbackOutcome.ConfigSaysNo:
    case RollbackOutcome.ConfigReadFailed:
    case RollbackOutcome.NoSourcesRecorded:
    case RollbackOutcome.NoSignedInUser:
    case RollbackOutcome.LatchAlreadyRead:
    case RollbackOutcome.LatchTakenDuringCopy:
    case RollbackOutcome.TargetRestarted:
    case RollbackOutcome.RecordWriteFailed:
    case RollbackOutcome.GenerationBumpMismatch:
    case RollbackOutcome.GenerationOutOfRange:
    case RollbackOutcome.ReArmGenerationUnknown:
    case RollbackOutcome.TailWriteFailed:
      return false;
  }
}

/** What the deciding session knows when it asks whether to request a rollback. */
export interface RollbackRequestInput {
  /** Capacitor native AND not iOS — the only devices that can have migrated */
  isAndroidNative: boolean;
  /** where this device's data is RIGHT NOW, from the latched engine answer */
  currentEngine: LocalDatabaseEngine;
  /** the signed-in user, or undefined when nobody is */
  uid: string | undefined;
  /** raw value of the rollback's do-not-try-again marker */
  blockedMarker: string | null | undefined;
  /** what `shouldRollBackThisDeviceToIndexedDb` answered for the two rollback controls */
  controlsSayRollBack: boolean;
}

/**
 * Should the DECIDING session write the rollback-requested marker?
 *
 * The mirror of {@link shouldRequestMigration}. The caller asks the cheap questions
 * once before it waits for the config as well, so a device that could never roll back
 * pays nothing for that wait; they are asked again here so this answer stands on its own.
 */
export function shouldRequestRollback(input: RollbackRequestInput): boolean {
  // a copy-back budget ran out on this device. Do not start again.
  if (input.blockedMarker) return false;

  // only an Android device can have migrated, so only one can roll back
  if (!input.isAndroidNative) return false;

  // already on IndexedDB: there is nothing to copy back
  if (input.currentEngine !== LocalDatabaseEngine.Sqlite) return false;

  // no signed-in user means no per-user config answer and no user database
  if (!input.uid) return false;

  return input.controlsSayRollBack;
}

/** What the rolling-back boot knows before it starts. */
export interface RollbackRunInput {
  /** raw value of the rollback-requested marker */
  requestedMarker: string | null | undefined;
  /** raw value of the rollback's do-not-try-again marker */
  blockedMarker: string | null | undefined;
  isAndroidNative: boolean;
  /**
   * From the RAW engine marker, never the latching accessor: the caller checks right
   * after this whether the latch has been taken, and reading through it would take it.
   */
  currentEngine: LocalDatabaseEngine;
  uid: string | undefined;
  /** has this page load already run the rollback? */
  alreadyRanThisPageLoad: boolean;
}

/**
 * Should THIS boot run the rollback's switch half?
 *
 * The mirror of {@link shouldRunMigrationThisBoot}. The engine check is the load-bearing
 * one: a device already back on IndexedDB must never copy a stale SQLite database over
 * the live IndexedDB one, whatever markers survived.
 */
export function shouldRunRollbackThisBoot(input: RollbackRunInput): boolean {
  if (input.currentEngine !== LocalDatabaseEngine.Sqlite) return false;

  // once per page load, like the migration's switch half
  if (input.alreadyRanThisPageLoad) return false;

  if (input.blockedMarker) return false;
  if (!input.requestedMarker) return false;
  if (!input.isAndroidNative) return false;
  if (!input.uid) return false;

  return true;
}

/**
 * What the rolled-back marker holds.
 *
 * **Two things with two different lifetimes, and nothing may conflate them.**
 *
 * - `generation` is the re-arm generation this device rolled back at. It stops the
 *   device migrating again straight away, and the hold ends at a re-arm — which is a
 *   COMPARISON against {@link resolveReArmGeneration}, never a deletion of this key.
 * - `supersededIndexedDbNames` are the IndexedDB databases the rollback left behind: the
 *   rollback copies into a FRESH #4401 generation, so the previous generation's
 *   databases — the pre-migration copy phase 3 deliberately kept — are now superseded and
 *   nothing else can name them. The app's own database provider iterates at the CURRENT
 *   generation, so it looks straight past them. R3's cleanup is what deletes them, and
 *   they are removed from here only by the cleanup that consumed them.
 *
 * The SQLite databases the rollback leaves behind are a different list and live
 * somewhere else — {@link RollbackCompletionEntry}'s `oldSqliteName` — because they are
 * deleted by the rollback's own tail, in the same page load, rather than after a grace
 * period.
 *
 * Getting that wrong in either direction is expensive. Delete the key at a re-arm and the
 * names go with it, orphaning a full duplicate of the user's database on disk with
 * nothing left recording it — the native plugin cannot list its own files, so nothing
 * would ever find them again. Keep the hold after a re-arm and the device can never
 * migrate again.
 */
export interface RolledBackMarker {
  generation: number;
  /** LOGICAL names (`userdb-fr_<uid>`), the same shape the sign-out record uses */
  supersededIndexedDbNames: string[];
}

/** Write a rolled-back marker. */
export function serialiseRolledBackMarker(marker: RolledBackMarker): string {
  return JSON.stringify({
    generation: marker.generation,
    supersededIndexedDbNames: [...marker.supersededIndexedDbNames],
  });
}

/**
 * Read a rolled-back marker, normalising anything an older or damaged value holds.
 *
 * Answers `undefined` only when there is no marker at all. Everything else is repaired
 * rather than refused: a bare number is the older shape {@link isMigrationHeldByRollback}
 * already accepts, a missing name list reads as empty, and a list holding anything that
 * is not a string drops those entries. A damaged marker must not throw on the boot path,
 * and it must not read as "nothing was ever rolled back" — that is the answer which starts
 * a fresh migration on a device that has just been taken off SQLite.
 *
 * **A damaged marker answers {@link UNKNOWN_ROLLBACK_GENERATION}, which really does hold.**
 * Saying "the floor holds it" would be wrong on this side of the comparison, and it would
 * be wrong in the direction that re-creates the loop — see that constant. Phase 3's private
 * `readRollbackGeneration`, which {@link isMigrationHeldByRollback} uses, reaches the same
 * answer by a different route: it reports the generation as unreadable and that function
 * holds outright. The two must never disagree about a damaged marker, because the obvious
 * later cleanup is to route the hold through this richer parser.
 */
export function readRolledBackMarker(
  storedValue: string | null | undefined,
): RolledBackMarker | undefined {
  if (storedValue === null || storedValue === undefined || storedValue === '') return undefined;

  const asBareNumber = Number.parseInt(storedValue, 10);
  if (Number.isFinite(asBareNumber) && String(asBareNumber) === storedValue.trim()) {
    return { generation: asBareNumber, supersededIndexedDbNames: [] };
  }

  let parsed: any;
  try {
    parsed = JSON.parse(storedValue);
  } catch {
    // A marker IS present and cannot be read. Answering {@link UNKNOWN_ROLLBACK_GENERATION}
    // keeps the device held back whatever the configured generation is, which is the
    // cautious direction on THIS side of the comparison — and an empty name list is the
    // only honest answer about databases this value can no longer name.
    return { generation: UNKNOWN_ROLLBACK_GENERATION, supersededIndexedDbNames: [] };
  }
  if (!parsed || typeof parsed !== 'object') {
    return { generation: UNKNOWN_ROLLBACK_GENERATION, supersededIndexedDbNames: [] };
  }

  const generation = typeof parsed.generation === 'number' && Number.isFinite(parsed.generation)
    ? parsed.generation
    : UNKNOWN_ROLLBACK_GENERATION;
  const names: string[] = Array.isArray(parsed.supersededIndexedDbNames)
    ? parsed.supersededIndexedDbNames.filter((name: unknown): name is string => typeof name === 'string')
    : [];
  return { generation, supersededIndexedDbNames: names };
}

/**
 * The re-arm generation the config says, or the value that holds every device.
 *
 * **The failure direction is the OPPOSITE of the percentages', and getting it backwards
 * re-creates the migrate-roll-back-migrate loop.** A percentage that cannot be read
 * resolves to 0, which migrates nobody. An unreadable re-arm generation must leave every
 * rolled-back device HELD, so it resolves to {@link MIGRATION_REARM_GENERATION} — the
 * floor, which every recorded generation is at or above. "Reads as 0 like the others"
 * would instantly re-arm every device that has ever been rolled back.
 *
 * A device is therefore re-armed only when the configured number is read successfully AND
 * is strictly greater than the one the device recorded.
 */
export function resolveReArmGeneration(configValue: unknown): number {
  return readConfiguredReArmGeneration(configValue) ?? MIGRATION_REARM_GENERATION;
}

/**
 * The re-arm generation the config says, or `undefined` when it does not say one.
 *
 * **Two callers want opposite things from an unreadable value, which is why this keeps
 * "could not be read" as its own answer instead of picking a number.**
 *
 * - The boot that asks whether a device is still HELD uses the value as the generation IN
 *   FORCE, and there the floor is cautious — it holds everybody. That is
 *   {@link resolveReArmGeneration}.
 * - The session that DECIDES to roll a device back records this value in the request
 *   marker, and it ends up in the rolled-back marker as the generation the device rolled
 *   back AT. On that side the floor is the least cautious value there is: a recorded 0
 *   against a configured 1 reads as re-armed, so the device would ask for a migration on
 *   the very page load its rollback commits. That caller must refuse to write anything
 *   rather than record a guess — there is no safely-high number to guess instead, and
 *   `RollbackOutcome.ReArmGenerationUnknown` is what it answers.
 *
 * Collapsing the two into one number is how the documented failure direction quietly
 * stopped holding, so the split is deliberate and neither caller may re-merge it.
 */
export function readConfiguredReArmGeneration(configValue: unknown): number | undefined {
  // An unusable value answers `undefined` — what counts as unusable, and why a shape
  // like `true` has to be refused before the conversion rather than after it, is
  // `readUsableConfigNumber`. No RANGE rule comes from there: a generation is not a
  // percentage, and its only bound is the floor applied below.
  const asNumber = readUsableConfigNumber(configValue);
  if (asNumber === undefined) return undefined;
  // A generation below the floor cannot re-arm anybody and is far more likely a typo than
  // an intention, so it reads as the floor rather than as a negative comparison.
  return Math.max(asNumber, MIGRATION_REARM_GENERATION);
}

/**
 * What the rollback left half-done for the next boot to finish, for ONE base name.
 *
 * Per base name, not one number for the whole device: generations are per database
 * (`localStoreGenerationKey(baseName)` is one key each) and a #4401 storage reset bumps
 * only the `user` database, so a device that has been through one carries different
 * generations on different databases.
 */
export interface RollbackCompletionEntry {
  /** the generation the reverse copy wrote into, and the one the device must move to */
  targetGeneration: number;
  /**
   * The SQLite database being left behind, by LOGICAL name, recorded explicitly.
   *
   * **Do not re-derive it later.** Once the generation is bumped, the live name list is
   * the NEW generation, so the sign-out deleter — which iterates the database provider at
   * the current generation — would issue deletes that match nothing and report success.
   */
  oldSqliteName: string;
}

/**
 * The rollback-completion record: one `localStorage` value, written in a single `setItem`
 * after the switch half's verification succeeds. Writing it is the COMMIT POINT of the
 * whole rollback.
 *
 * **Why one value rather than a sequence of writes.** The engine marker and the
 * generation are two independent `localStorage` values, and any order of two separate
 * writes has a fatal middle. Flip the marker first and a device killed in between reads
 * engine `idb` at the pre-rollback generation — the stale data. Bump first and it reads
 * engine `sqlite` at a generation no SQLite file has ever used — an empty database
 * followed by a full re-sync, with good copies sitting unread on both engines.
 */
export interface RollbackCompletionRecord {
  /** keyed by BASE name (`userdb-fr_<uid>`, with any generation suffix stripped) */
  entries: { [baseName: string]: RollbackCompletionEntry };
}

/** Write the rollback-completion record. */
export function serialiseRollbackCompletionRecord(record: RollbackCompletionRecord): string {
  return JSON.stringify({ entries: record.entries });
}

/**
 * Read the rollback-completion record, or `undefined` when there is nothing to finish.
 *
 * A damaged record answers `undefined`, which reads as "no tail outstanding" and leaves
 * the device exactly where it is — on SQLite, with its data intact. That is the safe
 * direction here, and it is the OPPOSITE of the rolled-back marker's: acting on half a
 * record could bump a generation whose counterpart the record can no longer name, which
 * is the empty-database failure this record exists to prevent. An entry missing either of
 * its two fields is dropped for the same reason, and a record left with no usable entry
 * at all answers `undefined`.
 */
export function readRollbackCompletionRecord(
  storedValue: string | null | undefined,
): RollbackCompletionRecord | undefined {
  if (!storedValue) return undefined;

  let parsed: any;
  try {
    parsed = JSON.parse(storedValue);
  } catch {
    // A record that will not parse answers "no tail outstanding", which leaves the device
    // exactly where it is. See this function's own note for why that is the safe direction
    // here and the opposite one from the rolled-back marker's.
    return undefined;
  }
  if (!parsed || typeof parsed !== 'object' || !parsed.entries
    || typeof parsed.entries !== 'object') {
    return undefined;
  }

  const entries: { [baseName: string]: RollbackCompletionEntry } = {};
  for (const [baseName, value] of Object.entries(parsed.entries as { [key: string]: any })) {
    if (!value || typeof value !== 'object') continue;
    if (typeof value.targetGeneration !== 'number' || !Number.isFinite(value.targetGeneration)) {
      continue;
    }
    if (typeof value.oldSqliteName !== 'string' || value.oldSqliteName === '') continue;
    entries[baseName] = {
      targetGeneration: value.targetGeneration,
      oldSqliteName: value.oldSqliteName,
    };
  }

  return Object.keys(entries).length > 0 ? { entries } : undefined;
}

/**
 * The re-arm generation a rollback request wrote down, or `undefined` when it cannot be
 * read. (#5270)
 *
 * The request marker's VALUE is that generation, decided on the session that had the
 * config loaded. Anything that is not a plain non-negative integer answers `undefined`,
 * which stops the rollback committing rather than guessing — see
 * {@link RollbackOutcome.ReArmGenerationUnknown} for why there is no safe guess.
 *
 * Deliberately NOT {@link readMigrationCounter}, which answers 0 for anything unreadable.
 * Zero is a legitimate generation here and is the one that holds every device, so
 * "unreadable" and "zero" must stay distinguishable.
 */
export function readRequestedReArmGeneration(
  storedValue: string | null | undefined,
): number | undefined {
  if (storedValue === null || storedValue === undefined) return undefined;
  const trimmed = storedValue.trim();
  const parsed = Number.parseInt(trimmed, 10);
  if (!Number.isFinite(parsed) || parsed < 0) return undefined;
  // the round trip refuses `12abc`, which `parseInt` would otherwise read as 12
  if (String(parsed) !== trimmed) return undefined;
  return parsed;
}

/** What step 3 of the tail should do for one base name. */
export enum GenerationMove {
  /** the device is already on the recorded target — do nothing and carry on */
  AlreadyThere = 'AlreadyThere',
  /** the device is exactly one generation below the target — bump it */
  Bump = 'Bump',
  /** the record cannot describe this device — stop, and do not touch the generation */
  OutOfRange = 'OutOfRange',
}

/**
 * What step 3 of the tail should do for this base name — asked BEFORE anything is moved.
 *
 * **Everything about this function is "decide first, act second", and that is not
 * optional.** `bumpLocalStoreGeneration` increments UNCONDITIONALLY and moves the app onto
 * the new name — the repo's own test pins that calling it twice yields two different names
 * — so anything discovered after calling it has been discovered too late. Two different
 * boots prove the point, and a bare bump-then-compare gets both wrong:
 *
 * - **A boot that resumed after step 3 had already run.** The stored generation IS the
 *   target. Bumping would push it to target+1, fail its own comparison, and leave the
 *   device on an empty database at a name nothing has ever written, for ever.
 * - **A boot whose stored generation is neither the target nor one below it.** Bumping
 *   moves the device one step closer to the target and one step further from its data, on
 *   every boot, each time opening an empty database that re-syncs from the server. The
 *   reachable route is a sign-out wipe: the completion record is on both `localStorage`
 *   survivor lists and the generation keys are on neither, so the record can survive
 *   naming a target the device is several generations below.
 *
 * So only the exactly-one-below case may bump, and the name comparison after the bump
 * stays as well — it catches the different failure of a generation write being REFUSED.
 */
export function decideGenerationMove(
  storedGeneration: number,
  entry: RollbackCompletionEntry,
): GenerationMove {
  if (storedGeneration === entry.targetGeneration) return GenerationMove.AlreadyThere;
  if (storedGeneration + 1 === entry.targetGeneration) return GenerationMove.Bump;
  return GenerationMove.OutOfRange;
}

// ---------------------------------------------------------------------------
// #4981 phase 4 (#5270) — R3: the grace period, and deleting the old IndexedDB copy.
//
// Phase 3 copies a device onto SQLite and deliberately KEEPS the IndexedDB data. This is
// what finally removes it, and the delay before it does is the only protection left
// against a SQLite copy that is wrong in a way the verification did not catch.
// ---------------------------------------------------------------------------

/**
 * How many successful boots a device must record before an IndexedDB copy it no longer uses
 * may be deleted.
 *
 * **Two paths spend this same number (#5270 step 6b).** A MIGRATED device counts its boots
 * on SQLite, and the number guards its pre-migration IndexedDB copy. A ROLLED-BACK device
 * counts its boots on the fresh IndexedDB generation it now uses, and the number guards the
 * generation it abandoned. {@link shouldCountReadyBoot} says why one counter is safe for
 * both.
 *
 * **What the number is protecting against, stated precisely, because it is easy to assume
 * the wrong thing.** It does NOT make rollback possible — R2's reverse copy writes fresh
 * IndexedDB data and does not read the old copy at all. It protects the narrower case
 * where the SQLite copy turns out to be WRONG in a way the document-for-document
 * verification did not catch, and the pre-migration data is the only correct copy left.
 *
 * **They are not required to be CONSECUTIVE**, and that is deliberate rather than an
 * oversight: a boot that wedges before `Ready` simply does not count, and nothing resets the
 * counter because of it. A device that wedges occasionally and recovers has still shown its
 * SQLite data is readable, which is what is being counted. Note the contrast with the abort
 * budget above, which IS consecutive and does reset — the two words are load-bearing here.
 *
 * Ten is a working week of ordinary use for somebody who opens the app once or twice a
 * day, which is long enough for a wrong copy to show itself as missing workouts or missing
 * food, and short enough that the duplicate is not carried for months. Nothing measured
 * picks this number; it is a judgement, and the cost of it being too large is disk space
 * while the cost of it being too small is data.
 */
export const REQUIRED_SQLITE_BOOTS_BEFORE_CLEANUP = 10;

/**
 * How many successful boots this device has recorded, reading the test override when one is
 * present. Not consecutive — see the constant above, 11 lines up, which says why.
 *
 * The override wins when it is larger, never when it is smaller: it is there to skip
 * ahead, and a stale small value must not undo a grace period the device has really
 * served. Anything unreadable in either value counts as zero, which is the safe direction
 * — it delays a deletion rather than bringing one forward.
 */
export function readSqliteReadyBoots(
  storedValue: string | null | undefined,
  testOverrideValue: string | null | undefined,
): number {
  return Math.max(
    readMigrationCounter(storedValue),
    readMigrationCounter(testOverrideValue),
  );
}

/** What the boot that reached `Ready` knows when it asks whether to count itself. */
export interface ReadyBootInput {
  /** where this device's data is RIGHT NOW, from the phase 2 marker */
  currentEngine: LocalDatabaseEngine;
  /**
   * Does the rolled-back marker still name IndexedDB databases waiting to be deleted?
   *
   * This is what tells a ROLLED-BACK device apart from one that simply never migrated.
   * Both are on IndexedDB; only the first has an abandoned copy to serve a grace period
   * for. See {@link shouldCountReadyBoot}.
   */
  hasSupersededIndexedDbNames: boolean;
  /** has this page load already counted itself? */
  alreadyCountedThisPageLoad: boolean;
}

/**
 * Should this boot reaching `Ready` count towards the grace period? (R3, widened by #5270
 * step 6b)
 *
 * **One counter, two grace periods, and the reason one key is safe for both.** The counter
 * always means "boots that reached `Ready` on the engine in use, since this device last
 * changed engine" — because the counter is reset on EVERY engine change: with
 * {@link ReadyBootCounterReset.RolledBack} on the way out of SQLite, and with
 * {@link ReadyBootCounterReset.ReMigrated} on the way back in. So the two meanings can
 * never be mixed in one stored number.
 *
 * - **On SQLite** it measures confidence in the SQLite copy, before the pre-migration
 *   IndexedDB copy is deleted. That is the forward cleanup.
 * - **On IndexedDB, with superseded names recorded**, the device has ROLLED BACK. It is
 *   running on a fresh IndexedDB generation, and R3 asks for the same grace period —
 *   counted the same way, on the generation now in use — before the generation it
 *   abandoned may be deleted. That is `superseded-idb-cleanup.ts`.
 *
 * **A device that never migrated counts nothing**, which is deliberate rather than
 * incidental: it has nothing to be confident about and no abandoned copy to delete, and
 * keeping it from writing the key at all is what makes "which devices carry this counter"
 * an answerable question.
 */
export function shouldCountReadyBoot(input: ReadyBootInput): boolean {
  if (input.alreadyCountedThisPageLoad) return false;
  if (input.currentEngine === LocalDatabaseEngine.Sqlite) return true;
  return input.hasSupersededIndexedDbNames;
}

/**
 * May this device delete its old IndexedDB copy? (R3)
 *
 * **Both conditions, and neither is optional.** The counter says the device has run long
 * enough on SQLite to be believed; the config key is the operator's switch, so a fleet that
 * starts losing data can be stopped from deleting anything more without an app release.
 *
 * Every unusable config value reads as OFF, which is the safe direction: the cost of a
 * wrong "no" is disk space, and the cost of a wrong "yes" is the user's only remaining
 * correct copy.
 */
export function mayDeleteLegacyIndexedDbCopy(
  readyBoots: number,
  cleanupEnabledValue: unknown,
): boolean {
  if (cleanupEnabledValue !== true) return false;
  return readyBoots >= REQUIRED_SQLITE_BOOTS_BEFORE_CLEANUP;
}

/** What the boot asking whether to delete a SUPERSEDED IndexedDB generation knows. */
export interface SupersededCleanupInput {
  /** rollback exists on Android only, so nothing else may reach this path */
  isAndroidNative: boolean;
  /** where this device's data is RIGHT NOW, from the phase 2 marker */
  currentEngine: LocalDatabaseEngine;
  /** how many names the rolled-back marker still carries */
  supersededNameCount: number;
  /** boots that reached `Ready` on the generation this device is using now */
  readyBoots: number;
  /** the operator's switch, `ConfigKey.AndroidSqliteCleanupEnabled`, raw from the config */
  cleanupEnabledValue: unknown;
}

/**
 * May this device delete the IndexedDB generation it abandoned when it ROLLED BACK?
 * (#5270 step 6b, R3)
 *
 * **The engine condition is the opposite way round from
 * {@link mayDeleteLegacyIndexedDbCopy}, and that is the whole danger of this path.** The
 * forward cleanup requires the device to be ON SQLite, where no `_pouch_` database is
 * live, which is what makes its unfiltered sweep safe. This one runs on a device that is
 * ON IndexedDB, whose LIVE databases sit under that same prefix beside the dead ones — so
 * the caller must delete recorded names only and must never run that sweep.
 * `superseded-idb-cleanup.ts` is a separate module for exactly that reason.
 *
 * **It reuses the SAME operator switch** as the forward cleanup rather than adding a
 * second one. The rollback runbook says to turn that switch off at the first sign of
 * missing data, and it has to stop BOTH deletions; a second switch is one more thing to
 * forget during an incident.
 *
 * Every unusable config value reads as OFF, the direction that only costs disk space.
 */
export function mayDeleteSupersededIndexedDbCopy(input: SupersededCleanupInput): boolean {
  if (!input.isAndroidNative) return false;
  // a device on SQLite is the FORWARD cleanup's business; its unfiltered sweep covers
  // every `_pouch_` database there, superseded generations included
  if (input.currentEngine !== LocalDatabaseEngine.IndexedDb) return false;
  if (input.supersededNameCount <= 0) return false;
  if (input.cleanupEnabledValue !== true) return false;
  return input.readyBoots >= REQUIRED_SQLITE_BOOTS_BEFORE_CLEANUP;
}

/**
 * Why the boot counter goes back to zero, for the resets the COMPONENT performs.
 *
 * **Both of these exist to stop a sequence that deletes live data**, and leaving either out
 * is how that happens: a device migrates, serves the whole grace period, rolls back (which
 * writes a FRESH IndexedDB copy), then re-migrates — and an unreset counter would permit the
 * cleanup to delete that fresh copy immediately, with no grace period at all for the copy it
 * actually has.
 *
 * **A completed sign-out resets the counter too, and is deliberately NOT a member here.** It
 * happens in `AuthService.signOutAsync`, which is a different module and cannot reach the
 * component's private helper — so it removes the key directly, as the last thing it does.
 * Listing it as a reason would send a reader looking for a third call site that does not
 * exist; the sign-out reset is stated where it happens, and in the survivor-list comments at
 * both ends of the apparent contradiction.
 */
export enum ReadyBootCounterReset {
  /** the device copied its data back off SQLite — the SQLite copy is no longer the one in use */
  RolledBack = 'RolledBack',
  /** the device moved onto SQLite again — this is a NEW copy, and it has served nothing */
  ReMigrated = 'ReMigrated',
}
